import {
  BellOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  NotificationOutlined,
  PlusOutlined,
  ReloadOutlined,
  TeamOutlined,
  UserOutlined,
} from '@ant-design/icons';
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  Modal,
  message,
  Popconfirm,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import type React from 'react';
import { useEffect, useState } from 'react';
import { API_URL } from '../config/api';
import 'dayjs/locale/hu';
import { useTranslation } from 'react-i18next';
import { dayjsLocale } from '../i18n/locale';

dayjs.extend(relativeTime);
// The global locale is set in `i18n/config.ts`, from the panel's language, so a
// bare `fromNow()` elsewhere — `FeedbackFormDetails` has one — now follows that
// language instead of always reading Hungarian. The locale data stays imported
// above: the per-call locale below is `hu` for Hungarian and Serbian.

const { Option } = Select;
const { TextArea } = Input;
const { Text, Title } = Typography;

interface ScheduledNotification {
  id: string;
  title: string;
  body: string;
  type: string;
  targetType: string;
  targetId?: string;
  scheduledFor: string;
  status: string;
  data?: any;
  createdAt: string;
  sentByUser?: {
    firstName: string;
    lastName: string;
    email: string;
  };
}

interface Event {
  id: string;
  title: string;
  startDate: string;
}

interface Session {
  id: string;
  title: string;
  startTime: string;
  event: {
    title: string;
  };
}

const ScheduledNotifications: React.FC = () => {
  const [scheduleModalVisible, setScheduleModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [scheduledNotifications, setScheduledNotifications] = useState<ScheduledNotification[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [refreshing, setRefreshing] = useState(false);
  const { t, i18n } = useTranslation();

  useEffect(() => {
    fetchScheduledNotifications();
    fetchEvents();
    fetchSessions();
  }, []);

  const fetchScheduledNotifications = async () => {
    setRefreshing(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/notifications/scheduled`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setScheduledNotifications(data.notifications || []);
      }
    } catch (error) {
      console.error('Failed to fetch scheduled notifications:', error);
    } finally {
      setRefreshing(false);
    }
  };

  const fetchEvents = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/events`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setEvents(data.data || []); // Fix: events are in data.data
      }
    } catch (error) {
      console.error('Failed to fetch events:', error);
    }
  };

  const fetchSessions = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/sessions`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setSessions(data || []); // Fix: sessions are directly in data
      }
    } catch (error) {
      console.error('Failed to fetch sessions:', error);
    }
  };

  const handleScheduleReminder = async (values: any) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');

      let endpoint = '';
      let body: any = {};

      if (values.reminderType === 'event') {
        endpoint = `${API_URL}/notifications/schedule/event-reminder`;
        const selectedEvent = events.find((e) => e.id === values.eventId);
        body = {
          eventId: values.eventId,
          eventStartTime: selectedEvent?.startDate,
          minutesBefore: values.minutesBefore || 30,
        };
      } else if (values.reminderType === 'session') {
        endpoint = `${API_URL}/notifications/schedule/session-reminder`;
        const selectedSession = sessions.find((s) => s.id === values.sessionId);
        body = {
          sessionId: values.sessionId,
          sessionStartTime: selectedSession?.startTime,
          minutesBefore: values.minutesBefore || 15,
        };
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      if (response.ok) {
        message.success(t('scheduledNotifications.scheduled'));
        setScheduleModalVisible(false);
        form.resetFields();
        fetchScheduledNotifications();
      } else {
        const error = await response.json();
        message.error(error.message || t('scheduledNotifications.scheduleFailed'));
      }
    } catch (error) {
      message.error(t('scheduledNotifications.scheduleFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleCancelNotification = async (notificationId: string) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/notifications/scheduled/${notificationId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        message.success(t('scheduledNotifications.deleted'));
        fetchScheduledNotifications();
      } else {
        const error = await response.json();
        message.error(error.message || t('scheduledNotifications.deleteFailed'));
      }
    } catch (error) {
      message.error(t('scheduledNotifications.deleteFailed'));
    }
  };

  const getTypeTag = (type: string) => {
    const typeConfig: Record<string, { color: string; icon: React.ReactNode; label: string }> = {
      EVENT_REMINDER: {
        color: 'blue',
        icon: <CalendarOutlined />,
        label: t('scheduledNotifications.typeEventReminder'),
      },
      SESSION_START: {
        color: 'green',
        icon: <ClockCircleOutlined />,
        label: t('scheduledNotifications.typeSessionStart'),
      },
      ANNOUNCEMENT: {
        color: 'purple',
        icon: <NotificationOutlined />,
        label: t('scheduledNotifications.typeAnnouncement'),
      },
    };

    const config = typeConfig[type] || { color: 'default', icon: null, label: type };
    return (
      <Tag color={config.color} icon={config.icon}>
        {config.label}
      </Tag>
    );
  };

  const columns: ColumnsType<ScheduledNotification> = [
    {
      title: t('scheduledNotifications.columnScheduledFor'),
      key: 'scheduledFor',
      width: 200,
      render: (record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{dayjs(record.scheduledFor).format('YYYY.MM.DD HH:mm')}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {dayjs(record.scheduledFor).locale(dayjsLocale(i18n.language)).fromNow()}
          </Text>
          {dayjs(record.scheduledFor).isBefore(dayjs()) && (
            <Badge status="error" text={t('scheduledNotifications.expired')} />
          )}
        </Space>
      ),
    },
    {
      title: t('common.type'),
      dataIndex: 'type',
      key: 'type',
      width: 150,
      render: (type) => getTypeTag(type),
    },
    {
      title: t('common.title'),
      dataIndex: 'title',
      key: 'title',
      width: 200,
      ellipsis: true,
      render: (title, record) => (
        <Tooltip
          title={title || t('scheduledNotifications.autoTitleTooltip', { type: record.type })}
        >
          <Text>{title || t('scheduledNotifications.autoTitle', { type: record.type })}</Text>
        </Tooltip>
      ),
    },
    {
      title: t('scheduledNotifications.columnTarget'),
      key: 'target',
      width: 150,
      render: (record) => {
        if (record.data?.eventId) {
          const event = events.find((e) => e.id === record.data.eventId);
          return (
            <Space>
              <CalendarOutlined />
              <Text>{event?.title || t('common.event')}</Text>
            </Space>
          );
        }
        if (record.data?.sessionId) {
          const session = sessions.find((s) => s.id === record.data.sessionId);
          return (
            <Space>
              <ClockCircleOutlined />
              <Text>{session?.title || t('scheduledNotifications.sessionFallback')}</Text>
            </Space>
          );
        }
        return <Text>-</Text>;
      },
    },
    {
      title: t('scheduledNotifications.columnReminder'),
      key: 'minutesBefore',
      width: 120,
      render: (record) => {
        const minutesBefore = record.data?.minutesBefore;
        if (minutesBefore) {
          return <Text>{t('scheduledNotifications.minutesBefore', { count: minutesBefore })}</Text>;
        }
        return <Text>-</Text>;
      },
    },
    {
      title: t('scheduledNotifications.columnCreatedAt'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 150,
      render: (createdAt) => <Text type="secondary">{dayjs(createdAt).format('MM.DD HH:mm')}</Text>,
    },
    {
      title: t('common.actions'),
      key: 'actions',
      width: 100,
      render: (record) => (
        <Space>
          <Popconfirm
            title={t('scheduledNotifications.deleteConfirm')}
            onConfirm={() => handleCancelNotification(record.id)}
            okText={t('common.yes')}
            cancelText={t('scheduledNotifications.cancel')}
          >
            <Button type="text" danger icon={<DeleteOutlined />} size="small" />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col span={24}>
          <Space style={{ width: '100%', justifyContent: 'space-between' }}>
            <div>
              <Title level={4} style={{ margin: 0 }}>
                {t('scheduledNotifications.title')}
              </Title>
              <Text type="secondary">{t('scheduledNotifications.subtitle')}</Text>
            </div>
            <Space>
              <Button
                icon={<PlusOutlined />}
                type="primary"
                onClick={() => setScheduleModalVisible(true)}
              >
                {t('scheduledNotifications.newReminder')}
              </Button>
              <Button icon={<ReloadOutlined />} onClick={fetchScheduledNotifications}>
                {t('scheduledNotifications.refresh')}
              </Button>
            </Space>
          </Space>
        </Col>
      </Row>

      {scheduledNotifications.length === 0 && !refreshing && (
        <Alert
          message={t('scheduledNotifications.emptyTitle')}
          description={t('scheduledNotifications.emptyDescription')}
          type="info"
          showIcon
          style={{ marginBottom: 24 }}
        />
      )}

      <Table
        columns={columns}
        dataSource={scheduledNotifications}
        rowKey="id"
        loading={refreshing}
        pagination={{
          defaultPageSize: 10,
          showSizeChanger: true,
          showTotal: (total) => t('scheduledNotifications.paginationTotal', { count: total }),
        }}
        scroll={{ x: 1000 }}
      />

      {/* Schedule Reminder Modal */}
      <Modal
        title={
          <>
            <PlusOutlined /> {t('scheduledNotifications.modalTitle')}
          </>
        }
        open={scheduleModalVisible}
        onCancel={() => {
          setScheduleModalVisible(false);
          form.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleScheduleReminder}>
          <Form.Item
            name="reminderType"
            label={t('scheduledNotifications.reminderTypeLabel')}
            rules={[{ required: true, message: t('scheduledNotifications.selectTypeRequired') }]}
          >
            <Select placeholder={t('scheduledNotifications.selectTypePlaceholder')} size="large">
              <Option value="event">
                <CalendarOutlined /> {t('scheduledNotifications.typeEventReminder')}
              </Option>
              <Option value="session">
                <ClockCircleOutlined /> {t('scheduledNotifications.optionSessionReminder')}
              </Option>
            </Select>
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) =>
              prevValues.reminderType !== currentValues.reminderType
            }
          >
            {({ getFieldValue }) => {
              const reminderType = getFieldValue('reminderType');

              if (reminderType === 'event') {
                return (
                  <Form.Item
                    name="eventId"
                    label={t('common.selectEvent')}
                    rules={[{ required: true, message: t('common.selectEventRequired') }]}
                  >
                    <Select placeholder={t('common.selectEvent')} size="large" showSearch>
                      {events.map((event) => (
                        <Option key={event.id} value={event.id}>
                          {event.title} - {dayjs(event.startDate).format('YYYY.MM.DD HH:mm')}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                );
              } else if (reminderType === 'session') {
                return (
                  <Form.Item
                    name="sessionId"
                    label={t('scheduledNotifications.selectSession')}
                    rules={[
                      {
                        required: true,
                        message: t('scheduledNotifications.selectSessionRequired'),
                      },
                    ]}
                  >
                    <Select
                      placeholder={t('scheduledNotifications.selectSession')}
                      size="large"
                      showSearch
                    >
                      {sessions.map((session) => (
                        <Option key={session.id} value={session.id}>
                          {session.title} - {session.event.title} -{' '}
                          {dayjs(session.startTime).format('MM.DD HH:mm')}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                );
              }
              return null;
            }}
          </Form.Item>

          <Form.Item
            name="minutesBefore"
            label={t('scheduledNotifications.timingLabel')}
            initialValue={30}
            rules={[{ required: true, message: t('scheduledNotifications.timingRequired') }]}
          >
            <Select size="large">
              <Option value={5}>{t('scheduledNotifications.minutesBefore', { count: 5 })}</Option>
              <Option value={15}>{t('scheduledNotifications.minutesBefore', { count: 15 })}</Option>
              <Option value={30}>{t('scheduledNotifications.minutesBefore', { count: 30 })}</Option>
              <Option value={60}>{t('scheduledNotifications.hoursBefore', { count: 1 })}</Option>
              <Option value={120}>{t('scheduledNotifications.hoursBefore', { count: 2 })}</Option>
              <Option value={1440}>{t('scheduledNotifications.daysBefore', { count: 1 })}</Option>
            </Select>
          </Form.Item>

          <Alert
            message={t('scheduledNotifications.autoAlertTitle')}
            description={t('scheduledNotifications.autoAlertDescription')}
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <Form.Item style={{ marginBottom: 0 }}>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button
                onClick={() => {
                  setScheduleModalVisible(false);
                  form.resetFields();
                }}
              >
                {t('scheduledNotifications.cancel')}
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                icon={<ClockCircleOutlined />}
              >
                {t('scheduledNotifications.scheduleButton')}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ScheduledNotifications;
